import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DialogActions,
  DialogContent,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  Stack,
  Typography,
} from '@mui/material';
import { Dialog as DsDialog } from '@natter-co/design-system';
import { useBreakpoints } from 'common/hooks';
import { VISUAL_REFRESH } from 'common/utils';
import { CloseIcon } from 'icons';

import { Button } from '../Buttons';
import { CopyTextField } from '../CopyTextField';
import { DialogCloseButton, DialogContentText, DialogSubtitle, DialogTitle, StyledDialogProps } from './Dialog.styled';
import { toDsProps } from './toDsProps';

import type { DialogProps as DsDialogProps } from '@natter-co/design-system';

type ShareOptions = {
  url: string;
  label?: string;
};

const ShareSection = ({ fields }: { fields: ShareOptions[] }) => {
  const { t } = useTranslation('common');
  return (
    <Stack gap={3} data-testid="share-section">
      {fields.map((option, index) => (
        <Stack key={index} gap={1} mt={option.label ? 0 : 2}>
          {option.label && (
            <Typography variant="natter-text-sm" fontWeight={600}>
              {option.label}
            </Typography>
          )}
          <CopyTextField value={option.url} label={option.label ?? t('copyLink')} labelHidden={Boolean(option.label)} />
        </Stack>
      ))}
    </Stack>
  );
};

export type DialogProps = Pick<
  MuiDialogProps,
  'fullWidth' | 'maxWidth' | 'role' | 'fullScreen' | 'sx' | 'disableRestoreFocus' | 'scroll'
> & {
  cancelLabel?: string;
  confirmLabel?: string;
  isConfirmLoading?: boolean;
  isConfirmDisabled?: boolean;
  isOpen: boolean;
  title?: ReactNode;
  hideHeader?: DsDialogProps['hideHeader'];
  bodyPadding?: DsDialogProps['bodyPadding'];
  closeLabel?: DsDialogProps['closeLabel'];
  subtitle?: string;
  description?: ReactNode;
  showCancelButton?: boolean;
  showCloseButton?: boolean;
  severity?: 'default' | 'danger';
  disableClickOutside?: boolean;
  formId?: string;
  shareOptions?: ShareOptions | ShareOptions[];
  /**
   * Which element takes focus when the dialog opens. Refresh-only, and passed to the DS dialog
   * unchanged: MUI already lands on the dialog container, so the legacy branch has nothing to do.
   */
  initialFocus?: DsDialogProps['initialFocus'];
  /**
   * Typed explicitly because TypeScript waves hyphenated JSX attributes through unchecked, so five
   * call sites have been passing one into the rest spread all along without it appearing in this
   * type. `toDsProps` has to be able to see it — see the note in `components/Dialog/types.ts`.
   */
  'data-testid'?: string;
  onClose?(): void;
  onClosed?(): void;
  onConfirm?(): void;
  Actions?: ReactNode;
} & StyledDialogProps;

export const Dialog = ({
  cancelLabel = 'Cancel',
  children,
  confirmLabel = 'Confirm',
  description,
  fullWidth,
  fullScreen,
  isOpen,
  isConfirmLoading,
  isConfirmDisabled,
  maxWidth = 'md',
  showCancelButton = true,
  showCloseButton = true,
  title,
  hideHeader = false,
  bodyPadding = 'default',
  closeLabel,
  subtitle,
  severity = 'default',
  variant,
  centered = variant === 'confirmation',
  disableClickOutside = false,
  formId,
  shareOptions = [],
  initialFocus,
  Actions,
  onClose,
  onClosed,
  onConfirm,
  ...dialogProps
}: PropsWithChildren<DialogProps>) => {
  const showConfirmButton = !!(onConfirm || formId);
  const showDialogActions = showCancelButton || showConfirmButton || Actions;
  const showDialogContent = description || children;
  const showShareSection = !!shareOptions;
  const shareFields = Array.isArray(shareOptions) ? shareOptions : [shareOptions];
  const { isMobile } = useBreakpoints();

  /**
   * The visual-refresh gate lives HERE, not at the call sites — all forty dialogs in the app reach
   * this component and none of them mention the flag. `toDsProps` returns `null` for anything DS 2.0
   * cannot express, which falls through to the MUI implementation below.
   */
  const dsProps = VISUAL_REFRESH
    ? toDsProps({
        isOpen,
        title,
        hideHeader,
        bodyPadding,
        closeLabel,
        maxWidth,
        fullScreen,
        showCloseButton,
        disableClickOutside,
        initialFocus,
        // Carries `sx`, `scroll`, `role`, `disableRestoreFocus` and `data-testid` — the props this
        // component never reads itself and passes straight to MUI. The mapper refuses `sx`, ignores
        // `scroll`, maps `role` and `disableRestoreFocus` (onto `finalFocus`), and forwards
        // `data-testid`.
        ...dialogProps,
      })
    : null;

  /**
   * The DS component reports `open`; the legacy API only has "closed". Opening is driven by the call
   * site setting `isOpen`, so there is nothing to report back for it — which is why this drops the
   * `true` case rather than exposing an `onOpen`.
   */
  const handleOpenChange = useCallback((nextOpen: boolean) => !nextOpen && onClose?.(), [onClose]);

  if (dsProps) {
    // `subtitle` is the one line the MUI dialog drew between the heading and the body. In the DS
    // dialog that slot is `description`, which is also what announces it, so it folds in there.
    const dsDescription =
      subtitle && description ? (
        <>
          <span className="block">{subtitle}</span>
          <span className="mt-2 block">{description}</span>
        </>
      ) : (
        (description ?? subtitle)
      );

    return (
      <DsDialog
        {...dsProps}
        description={dsDescription}
        /**
         * Built from the SAME legacy `Button`s the MUI branch uses, which refresh themselves through
         * their own wrapper — so the footer needs no DS vocabulary here. Two deliberate departures:
         *
         *   - cancel is `outlined-grey` (the DS secondary pill), not the borderless text button Figma
         *     draws (544:20045): borderless took the full foreground colour and read as heavy as the
         *     title. Edo chose the pill over a quieter-text option on 2026-10-06; the Figma set is to follow.
         *   - no `fullWidth`. On a phone the DS footer stacks its actions full width at 44px with the
         *     primary on top (`stackFooterOnMobile` defaults on), so the buttons need no width of their
         *     own and stay auto-width in the row from `sm` up.
         */
        footer={
          showDialogActions ? (
            <>
              {showCancelButton && (
                <Button variant="outlined-grey" onClick={onClose} disabled={isConfirmLoading}>
                  {cancelLabel}
                </Button>
              )}
              {showConfirmButton && (
                <Button
                  variant="contained"
                  color={severity === 'danger' ? 'error' : 'primary'}
                  isLoading={isConfirmLoading}
                  onClick={formId ? undefined : onConfirm}
                  type={formId ? 'submit' : undefined}
                  form={formId}
                  disabled={isConfirmDisabled}
                >
                  {confirmLabel}
                </Button>
              )}
              {Actions}
            </>
          ) : undefined
        }
        open={isOpen}
        onOpenChange={handleOpenChange}
        // The legacy prop is MUI's transition callback, which the DS dialog has no equivalent of by
        // that name; Base UI's `onOpenChangeComplete` fires at the same point in the lifecycle.
        onOpenChangeComplete={onClosed ? (open) => !open && onClosed() : undefined}
      >
        {children}
        {shareFields.length > 0 && <ShareSection fields={shareFields} />}
      </DsDialog>
    );
  }

  return (
    <MuiDialog
      open={isOpen}
      onClose={disableClickOutside ? undefined : onClose}
      disableEscapeKeyDown={disableClickOutside}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      onTransitionExited={onClosed}
      onClick={(e) => {
        e.stopPropagation();
      }}
      {...dialogProps}
    >
      {hideHeader ? (
        <span id="dialog-title" className="sr-only">
          {title}
        </span>
      ) : (
        <DialogTitle id="dialog-title" centered={centered} fullScreen={fullScreen}>
          {title}
          {showCloseButton && (
            <DialogCloseButton onClick={onClose} aria-label={closeLabel ?? 'Close'}>
              <CloseIcon fontSize="medium" />
            </DialogCloseButton>
          )}
        </DialogTitle>
      )}
      {subtitle && <DialogSubtitle pb={2}>{subtitle}</DialogSubtitle>}
      {showDialogContent && (
        <DialogContent
          data-testid="dialog-content"
          sx={bodyPadding === 'none' ? { p: 0, display: 'flex', flexDirection: 'column' } : undefined}
        >
          {description && (
            <DialogContentText id="dialog-description" centered={centered}>
              {description}
            </DialogContentText>
          )}

          {children}

          {showShareSection && shareFields && <ShareSection fields={shareFields} />}
        </DialogContent>
      )}
      {showDialogActions && (
        <DialogActions
          sx={({ breakpoints }) => ({
            [breakpoints.down('md')]: {
              flexDirection: 'column-reverse',
              gap: 2,
              '&>button': { marginLeft: '0 !important' },
            },
            // Buttons passed through `Actions` stretch where the built-in ones get `fullWidth`
            // (`isMobile`), so a stacked footer never mixes full-width and intrinsic-width buttons.
            [breakpoints.down('sm')]: {
              '&>.MuiButton-root': { width: '100%' },
            },
          })}
        >
          {showCancelButton && (
            <Button
              variant="outlined-grey"
              color="secondary"
              onClick={onClose}
              fullWidth={centered || isMobile}
              disabled={isConfirmLoading}
            >
              {cancelLabel}
            </Button>
          )}
          {showConfirmButton && (
            <Button
              variant="contained"
              color={severity === 'danger' ? 'error' : 'primary'}
              isLoading={isConfirmLoading}
              fullWidth={centered || isMobile}
              onClick={formId ? undefined : onConfirm}
              type={formId ? 'submit' : undefined}
              form={formId}
              disabled={isConfirmDisabled}
            >
              {confirmLabel}
            </Button>
          )}
          {Actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
};
